import { baseConfig } from '../../config/algolia';
import { AlgoliaTrackingCartTransformer } from '../cart/AlgoliaTrackingCartTransformer';
import { AlgoliaProductHit } from '../types';
import { SessionStorageKeys, getSession, setSession } from '../sessionStorage';
import aa from 'search-insights';
import Cookies from 'js-cookie';
import { JbCart } from '@jbhi-fi/apis/src/shopify/types/JbCart';

const CHUNK_SIZE = 20; // Algolia only allows 20 objectIDs per request

const loadInsights = () => {
  return new Map<string, string>(getSession(SessionStorageKeys.AlgoliaInsights, true));
};

const saveInsights = (insights: Map<string, string>) => {
  setSession(SessionStorageKeys.AlgoliaInsights, [...insights], true);
};

const isCollectionPage = window.location.pathname.includes('/collections/');

export const EVENTS = {
  SEARCH_CLICK: 'Product clicked after search',
  COLLECTION_CLICK: 'Product clicked after collection',
  PRODUCT_CLICK: 'Product clicked',
  SEARCH_ADDTOCART: 'Product added to cart after search',
  COLLECTION_ADDTOCART: 'Product added to cart after collection',
  PRODUCT_ADDTOCART: 'Product added to cart',
  SEARCH_PURCHASE: 'Product purchased after search',
  PRODUCT_PURCHASE: 'Product purchased',
  PRODUCT_VIEW: 'Product viewed',
  FILTER_VIEW: 'Filter viewed',
  FILTER_CLICK: 'Filter applied',
  FILTER_CONVERSION: 'Filter conversion',
} as const;

/**
 * Adds a query id to session storage preserve insights for conversion tracking.
 */
export const addInsight = (sku: string, queryId?: string) => {
  if (!queryId) return;
  const insights = loadInsights();
  insights.set(sku, queryId);
  saveInsights(insights);
};

export const getInsight = (sku: string) => {
  const insights = loadInsights();
  return insights.get(sku);
};

const removeInsight = (sku: string) => {
  const insights = loadInsights();
  insights.delete(sku);
  saveInsights(insights);
};

export const initialiseInsights = () => {
  const userToken = Cookies.get('_ALGOLIA');
  const authToken = window.Jbhifi?.customerId;

  aa('init', {
    appId: baseConfig.app_id,
    apiKey: baseConfig.search_api_key,
    useCookie: true,
    userToken: authToken ?? userToken,
    authenticatedUserToken: authToken,
  });
};

// Store the current search refinements to be used for insights tracking, as we don't always have access to the search state.
export const setSearchRefinements = (
  categoryRefinements: Record<string, Array<string>> | undefined,
  rangeRefinements: Record<string, string> | undefined
) => {
  window.Jbhifi.currentSearchRefinements = {
    categoryRefinements: categoryRefinements ?? {},
    rangeRefinements: rangeRefinements ?? {},
  };
};

export const getSearchRefinements = () => {
  const refinements = window.Jbhifi.currentSearchRefinements;
  if (!refinements) return [];
  const refinementsArray: Array<string> = [];
  Object.entries(refinements.categoryRefinements || {}).forEach(([key, values]) => {
    values.forEach(value => {
      refinementsArray.push(`${key.replace('facets.', '')}:${value}`);
    });
  });
  Object.entries(refinements.rangeRefinements || {}).forEach(([key, value]) => {
    refinementsArray.push(`${key.replace('facets.', '')}:${value}`);
  });
  return refinementsArray;
};

export const postAlgoliaProductClick = (sku: string, position?: number, queryID?: string) => {
  if (queryID) {
    aa('clickedObjectIDsAfterSearch', {
      eventName: isCollectionPage ? EVENTS.COLLECTION_CLICK : EVENTS.SEARCH_CLICK,
      index: baseConfig.product_families_index ?? '',
      objectIDs: [sku],
      positions: [position ?? 0],
      queryID,
    });
  } else {
    aa('clickedObjectIDs', {
      eventName: EVENTS.PRODUCT_CLICK,
      index: baseConfig.product_families_index ?? '',
      objectIDs: [sku],
    });
  }
};

export const postAlgoliaProductView = (hits: Array<AlgoliaProductHit>) => {
  if (!hits.length) return;

  const index = baseConfig.product_families_index ?? '';

  for (let i = 0; i < hits.length; i += CHUNK_SIZE) {
    const chunk = hits.slice(i, i + CHUNK_SIZE);
    const objectIDs = chunk.map(hit => hit.sku || hit.skus?.[0]) as Array<string>;

    aa('viewedObjectIDs', {
      eventName: EVENTS.PRODUCT_VIEW,
      index: index,
      objectIDs: objectIDs,
    });
  }
};

// queryId is optional because it is not always available when a product is added to the cart. In this case, the queryId is pulled from local storage.
export const postAlgoliaAddToCart = (sku: string, queryID?: string) => {
  if (queryID) {
    aa('addedToCartObjectIDsAfterSearch', {
      eventName: isCollectionPage ? EVENTS.COLLECTION_ADDTOCART : EVENTS.SEARCH_ADDTOCART,
      index: baseConfig.product_families_index ?? '',
      objectIDs: [sku],
      queryID: queryID,
    });
  } else {
    aa('addedToCartObjectIDs', {
      eventName: EVENTS.PRODUCT_ADDTOCART,
      index: baseConfig.product_families_index ?? '',
      objectIDs: [sku],
    });
  }
};

export const postAlgoliaPurchase = (cart: JbCart) => {
  const cartItemsWithQueryIds = cart.items.map(item => ({
    ...item,
    queryId: getInsight(item.sku),
  }));
  const nonSearchProducts = cartItemsWithQueryIds.filter(item => !item.queryId);
  if (nonSearchProducts.length > 0) {
    aa('purchasedObjectIDs', {
      ...AlgoliaTrackingCartTransformer.transformCartForAlgoliaTracking(nonSearchProducts),
      eventName: EVENTS.PRODUCT_PURCHASE,
    });
  }

  const searchProducts = cartItemsWithQueryIds.filter(item => item.queryId);
  if (searchProducts.length > 0) {
    aa('purchasedObjectIDsAfterSearch', {
      ...AlgoliaTrackingCartTransformer.transformCartForAlgoliaTracking(searchProducts),
      eventName: EVENTS.SEARCH_PURCHASE,
    });
    searchProducts.forEach(item => removeInsight(item.sku));
  }
};

export const postAlgoliaFilterView = (filter: string) => {
  aa('viewedFilters', {
    eventName: EVENTS.FILTER_VIEW,
    index: baseConfig.product_families_index ?? '',
    filters: [`${filter}:all`],
  });
};

export const postAlgoliaFilterClick = (filter: string, filterValue: string | number) => {
  aa('clickedFilters', {
    eventName: EVENTS.FILTER_CLICK,
    index: baseConfig.product_families_index ?? '',
    filters: [`${filter}:${filterValue}`],
  });
};

export const postAlgoliaFilterConvert = () => {
  const filters = getSearchRefinements();
  if (!filters.length) return;
  aa('convertedFilters', {
    eventName: EVENTS.FILTER_CONVERSION,
    index: baseConfig.product_families_index ?? '',
    filters,
  });
};

/**
 * Helper function to get the information required for analytics tags.
 * @returns {Array<string>} - Returns an array of strings with the platform, browser, operating system, country, region, and perks status.
 */
export const getAnalyticsTags = (searchType: 'discovery' | 'recai' | 'search') => {
  const { userAgent } = navigator;

  const platform = 'ontouchstart' in window || navigator.maxTouchPoints > 0 ? 'Mobile' : 'Desktop';

  let browser = 'unknown';
  if (userAgent.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Edg/')) {
    browser = 'Edge';
  } else if (userAgent.includes('Chrome/')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Safari/')) {
    browser = 'Safari';
  }

  let operatingSystem = 'unknown';
  if (/(iPad|iPhone|iPod)/g.test(userAgent)) {
    operatingSystem = 'iOS';
  } else if (userAgent.includes('Android')) {
    operatingSystem = 'Android';
  } else if (userAgent.includes('Windows NT')) {
    operatingSystem = 'Windows';
  } else if (userAgent.includes('Mac OS X')) {
    operatingSystem = 'Mac OS';
  } else if (userAgent.includes('Linux')) {
    operatingSystem = 'Linux';
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const country = timezone.split('/')[0] || 'Unknown';
  const region = timezone.split('/')[1] || 'Unknown';

  const isPerks = window.Jbhifi.customerDetails?.isPerks ? 'perks_yes' : 'perks_no';

  return [platform, browser, operatingSystem, country, region, isPerks, searchType];
};
